import axios from "axios";
import { api } from "../config";
import Cookies from "js-cookie";

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: api.API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const getStoredToken = () => {
  try {
    return Cookies.get("authToken") || null;
  } catch (error) {
    console.error("Error reading auth token from cookies:", error);
    return null;
  }
};

export const getLoggedinUser = () => {
  try {
    const authUser = Cookies.get("authUser");
    return authUser ? JSON.parse(authUser) : null;
  } catch (error) {
    console.error("Error reading auth user from cookies:", error);
    return null;
  }
};

// Initialize auth header if token exists
const token = getStoredToken();
if (token) {
  axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const extractBackendError = (responseData) => {
  if (!responseData) return null;

  const err = responseData.error;
  if (err && typeof err === "object" && typeof err.message === "string") {
    return { message: err.message, code: err.code, details: err.details };
  }
  if (typeof responseData.message === "string") {
    return { message: responseData.message, code: undefined, details: responseData.details };
  }
  return null;
};

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response.data || response,
  (error) => {
    const backendError = extractBackendError(error.response?.data);

    const errorMessage = {
      message: backendError?.message || "An error occurred",
      status: error.response?.status,
      code: backendError?.code,
      details: backendError?.details,
    };

    switch (error.response?.status) {
      case 401:
        errorMessage.message = backendError?.message || "Invalid credentials";
        Cookies.remove("authToken");
        Cookies.remove("authUser");
        break;
      case 404:
        errorMessage.message = backendError?.message || "Resource not found";
        break;
      case 500:
        errorMessage.message = backendError?.message || "Internal Server Error";
        break;
      default:
        errorMessage.message = backendError?.message || error.message;
    }

    return Promise.reject(errorMessage);
  }
);

export const setAuthorization = (token) => {
  if (token) {
    Cookies.set("authToken", token, { expires: 7, sameSite: "Strict" });
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    Cookies.remove("authToken");
    delete axiosInstance.defaults.headers.common["Authorization"];
  }
};

class APIClient {
  async get(url, params) {
    try {
      return await axiosInstance.get(url, params);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async create(url, data) {
    try {
      return await axiosInstance.post(url, data);
    } catch (error) {
      console.log(error)
      throw this.handleError(error);
    }
  }

  async createMail(url, data) {
    try {
      return await axiosInstance.post(url, data);
    } catch (error) {
      console.log(error)
      throw this.handleErrorMail(error);
    }
  }

  async createFormData(url, data) {
    try {
      return await axiosInstance.post(url, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async update(url, data) {
    try {
      return await axiosInstance.patch(url, data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async put(url, data) {
    try {
      return await axiosInstance.put(url, data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async delete(url, config = {}) {
    try {
      return await axiosInstance.delete(url, config);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Special method for user creation that uses fetch instead of axios
  async createUser(url, data) {
    try {
      const response = await fetch(`${api.API_URL}${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  handleError(error) {
    // `error` here is whatever the response interceptor rejected with —
    // {message, status, code, details} once it's gone through
    // extractBackendError, so error.message is already the real backend
    // message (not a generic fallback) whenever the backend provided one.
    const err = new Error(error?.message || "Network Error");
    err.status = error?.status;
    err.code = error?.code;
    err.details = error?.details;
    return err;
  }

  handleErrorMail(error) {
    console.log(error)
    if (error) {
      const statusCode = error.status;
      const errorMessage = error.message || "An error occurred";

      return { status: statusCode, message: errorMessage };
  }

  return { status: 500, message: error.message || "Network Error" };
  }

}

export const apiClient = new APIClient();
